import { adminFetch } from "./_client";
import type { PayoutSummary, PayoutCalendarResponse, PayoutConnectHoldItem, PayoutTripDetail, PayoutHostListItem, AdminPayoutLinkItem, PayoutHostDetail, PayoutFailureItem, PayoutDriftResponse, PayoutDriftReconcileResult, PayoutApprovalsResponse, PendingLiabilityByHost, PayoutPolicyCountryRow, PayoutPolicyCountryUpdate, CapabilityRefreshResult, HostPayoutOverrideResponse, HostPayoutOverrideUpdate, ManualPayoutQueueItem, ManualPayoutDetail, ManualPayoutApproveRequest } from "./types";


export function fetchPayoutSummary(token: string) {
  return adminFetch<PayoutSummary>(`/admin/payouts/summary`, token);
}

export function fetchPayoutLiabilityByHost(token: string) {
  return adminFetch<PendingLiabilityByHost>(`/admin/payouts/liability/by-host`, token);
}

/** Read-only custom-quote payment evidence. The server fixes the window at the
 * most recent 48 hours and selects successful renter payments only. */
export function fetchCustomQuotePaymentEvidence(token: string, params: { page?: number; pageSize?: number } = {}) {
  const sp = new URLSearchParams();
  if (params.page) sp.set("page", String(params.page));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  const qs = sp.toString();
  return adminFetch<import("./types").CustomQuotePaymentEvidenceResponse>(
    `/admin/payouts/custom-quote-payments${qs ? `?${qs}` : ""}`,
    token,
  );
}

/** Selected-trip payment attempts/errors for the evidence view. This endpoint
 * is read-only and returns only renter-payment operation rows. */
export function fetchCustomQuotePaymentEvidenceDetail(token: string, tripId: number) {
  return adminFetch<import("./types").CustomQuotePaymentEvidenceDetail>(
    `/admin/payouts/custom-quote-payments/trip/${tripId}`,
    token,
  );
}


export function fetchPayoutCalendar(token: string, params: {
  from?: string;
  to?: string;
  /** Task #6515 — independent upcoming window start (default: today). */
  upcomingFrom?: string;
  /** Task #6515 — independent upcoming window end (default: today+14d). */
  upcomingTo?: string;
  /** Task #6515 — independent past window start (default: today−7d). */
  pastFrom?: string;
  /** Task #6515 — independent past window end (default: today). */
  pastTo?: string;
  region?: "us" | "intl" | null;
  status?: "held" | "pending" | null;
  /** Task #6515 — when true, fetches all payout_status=3 trips uncapped and
   *  returns them in heldItems for the "Awaiting host account setup" section. */
  includeAllHeld?: boolean;
} = {}) {
  const sp = new URLSearchParams();
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.upcomingFrom) sp.set("upcomingFrom", params.upcomingFrom);
  if (params.upcomingTo) sp.set("upcomingTo", params.upcomingTo);
  if (params.pastFrom) sp.set("pastFrom", params.pastFrom);
  if (params.pastTo) sp.set("pastTo", params.pastTo);
  if (params.region) sp.set("region", params.region);
  if (params.status) sp.set("status", params.status);
  if (params.includeAllHeld) sp.set("includeAllHeld", "true");
  const qs = sp.toString();
  return adminFetch<PayoutCalendarResponse>(`/admin/payouts/calendar${qs ? `?${qs}` : ""}`, token);
}

/** Task #6515 — list all open Connect holds from payout_connect_holds. */
export function fetchPayoutConnectHolds(token: string) {
  return adminFetch<{ items: PayoutConnectHoldItem[] }>(`/admin/payouts/connect-holds`, token);
}

/** Task #6563 — dismiss a single Connect hold row (e.g. trip settled off-Connect). */
export function resolveConnectHold(token: string, holdId: number, reason: string) {
  return adminFetch<{ ok: true }>(`/admin/payouts/connect-holds/${holdId}/resolve`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

/** Task #6563 — bulk-settle legacy pre-cutover held trips to payout_status=2. */
export function bulkSettleLegacyHolds(token: string, tripIds: number[], reason: string) {
  return adminFetch<{ ok: true; affected: number; skipped: number }>(
    "/admin/payouts/legacy-holds/bulk-settle",
    token,
    { method: "POST", body: JSON.stringify({ trip_ids: tripIds, reason }) },
  );
}


export function fetchPayoutTripDetail(token: string, tripId: number) {
  return adminFetch<PayoutTripDetail>(`/admin/payouts/calendar/trip/${tripId}`, token);
}


export function holdTripPayout(token: string, tripId: number, reason: string) {
  return adminFetch<{ ok: true; actionId: number | null; alreadyHeld?: boolean }>(`/admin/payouts/trip/${tripId}/hold`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function releaseTripPayout(token: string, tripId: number, reason: string) {
  return adminFetch<{ ok: true; actionId: number | null }>(`/admin/payouts/trip/${tripId}/release`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function retryTripPayout(token: string, tripId: number, reason?: string, force = false) {
  return adminFetch<{ ok: true; actionId: number | null; status: "queued"; message: string }>(`/admin/payouts/trip/${tripId}/retry`, token, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? "manual_admin_retry", force }),
  });
}


export function fetchPayoutHosts(token: string, params: { q?: string; page?: number; pageSize?: number; filter?: "all" | "on_hold" | "pending" | "at_risk" | "no_connect_ds" } = {}) {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.page) sp.set("page", String(params.page));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  if (params.filter && params.filter !== "all") sp.set("filter", params.filter);
  const qs = sp.toString();
  return adminFetch<{ items: PayoutHostListItem[]; page: number; pageSize: number; total: number }>(`/admin/payouts/hosts${qs ? `?${qs}` : ""}`, token);
}


export function fetchPayoutHostDetail(token: string, hostId: string) {
  return adminFetch<PayoutHostDetail>(`/admin/payouts/hosts/${encodeURIComponent(hostId)}`, token);
}


// Task #1092 Phase B — admin payout-link history for a host.
export function fetchHostPayoutLinks(token: string, hostId: string) {
  return adminFetch<{ items: AdminPayoutLinkItem[] }>(`/admin/payouts/hosts/${encodeURIComponent(hostId)}/payout-links`, token);
}


// Task #1092 Phase B — admin re-sends a host's open payout link.
export function sendResendPayoutLink(token: string, hostId: string, reason?: string) {
  return adminFetch<{ ok: true; actionId: number | null; payoutLinkId: number; url: string; usedFallback: boolean }>(
    `/admin/payouts/hosts/${encodeURIComponent(hostId)}/resend-payout-link`,
    token,
    { method: "POST", body: JSON.stringify({ reason: reason ?? "admin_resend" }) },
  );
}


export function adjustHostPayout(token: string, hostId: string, body: { kind: "credit" | "debit"; amount: number; currency: string; reason: string; trip_id?: number | null }) {
  return adminFetch<{ ok: true; actionId: number | null }>(`/admin/payouts/hosts/${encodeURIComponent(hostId)}/adjust`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function sendHostConnectLink(token: string, hostId: string, body: { trip_id?: number | null; message?: string } = {}) {
  return adminFetch<{ ok: true; sentViaConversation: boolean }>(`/admin/payouts/hosts/${encodeURIComponent(hostId)}/connect-link`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


export function holdAllHostPayouts(token: string, hostId: string, reason: string) {
  return adminFetch<{ ok: true; affected: number }>(`/admin/payouts/hosts/${encodeURIComponent(hostId)}/hold-all`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function fetchPayoutFailures(token: string, params: { page?: number; pageSize?: number; region?: "us" | "intl" | null } = {}) {
  const sp = new URLSearchParams();
  if (params.page) sp.set("page", String(params.page));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  if (params.region) sp.set("region", params.region);
  const qs = sp.toString();
  return adminFetch<{ items: PayoutFailureItem[]; page: number; pageSize: number; total: number }>(`/admin/payouts/failures${qs ? `?${qs}` : ""}`, token);
}


// Task #4093 — owner-payout drift reconciliation queue.
export function fetchPayoutDrift(token: string, params: { includeResolved?: boolean } = {}) {
  const qs = params.includeResolved ? "?include=resolved" : "";
  return adminFetch<PayoutDriftResponse>(`/admin/payouts/drift${qs}`, token);
}

export function reconcilePayoutDrift(
  token: string,
  tripId: number,
  body: { action: "pay_delta" | "resolve"; note: string },
) {
  return adminFetch<PayoutDriftReconcileResult>(`/admin/payouts/drift/${tripId}/reconcile`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}


// Task #4796 — manual payout-approval gate. Queue of host payouts awaiting an
// explicit admin GO/NO-GO before any Stripe money movement.
export function fetchPayoutApprovals(token: string, params: { status?: string } = {}) {
  const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : "";
  return adminFetch<PayoutApprovalsResponse>(`/admin/payouts/approvals${qs}`, token);
}

export function approvePayoutApproval(token: string, id: number, reason: string) {
  return adminFetch<{ ok: true; status: string; alreadyResolved?: boolean; triggered?: { rail: string; result: unknown } }>(
    `/admin/payouts/approvals/${id}/approve`,
    token,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

export function holdPayoutApproval(token: string, id: number, reason: string) {
  return adminFetch<{ ok: true; status: string }>(`/admin/payouts/approvals/${id}/hold`, token, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}


export function fetchPayoutPolicyCountries(token: string) {
  return adminFetch<{ items: PayoutPolicyCountryRow[]; capabilityRefreshedAt: string | null }>(
    "/admin/payout-policy/countries",
    token,
  );
}


export function updatePayoutPolicyCountry(token: string, code: string, body: PayoutPolicyCountryUpdate) {
  return adminFetch<{ success: boolean; code: string }>(
    `/admin/payout-policy/countries/${encodeURIComponent(code)}`,
    token,
    { method: "PUT", body: JSON.stringify(body) },
  );
}


export function refreshPayoutPolicyCapabilities(token: string) {
  return adminFetch<CapabilityRefreshResult>(
    "/admin/payout-policy/capabilities/refresh",
    token,
    { method: "POST" },
  );
}


export function fetchHostPayoutOverrides(token: string, hostId: string) {
  return adminFetch<HostPayoutOverrideResponse>(
    `/admin/payout-policy/hosts/${encodeURIComponent(hostId)}/overrides`,
    token,
  );
}


export function updateHostPayoutOverrides(token: string, hostId: string, body: HostPayoutOverrideUpdate) {
  return adminFetch<{ ok: true; hostId: string; updates: Record<string, boolean | null> }>(
    `/admin/payout-policy/hosts/${encodeURIComponent(hostId)}/overrides`,
    token,
    { method: "PUT", body: JSON.stringify(body) },
  );
}


export function fetchManualPayoutQueue(
  token: string,
  params: { country?: string | null; q?: string | null; limit?: number } = {},
) {
  const qs = new URLSearchParams();
  if (params.country) qs.set("country", params.country);
  if (params.q) qs.set("q", params.q);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return adminFetch<{ items: ManualPayoutQueueItem[]; total: number }>(
    `/admin/payouts/manual${suffix}`,
    token,
  );
}


export function fetchManualPayoutDetail(token: string, tripId: number) {
  return adminFetch<ManualPayoutDetail>(
    `/admin/payouts/manual/${tripId}`,
    token,
  );
}


export function approveManualPayout(token: string, tripId: number, body: ManualPayoutApproveRequest) {
  return adminFetch<{
    ok: true;
    manual_payout_id: number;
    billing_id: number | null;
    has_discrepancy: boolean;
    amount_usd: number | null;
  }>(
    `/admin/payouts/manual/${tripId}/approve`,
    token,
    { method: "POST", body: JSON.stringify(body) },
  );
}


export function flagManualPayoutDiscrepancy(token: string, tripId: number, reason: string) {
  return adminFetch<{ ok: true; manual_payout_id: number }>(
    `/admin/payouts/manual/${tripId}/discrepancy`,
    token,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}


/** Build the CSV download URL with auth — caller fetches it as a blob. */
export function manualPayoutCsvUrl(params: { since?: string; until?: string } = {}): string {
  const qs = new URLSearchParams();
  if (params.since) qs.set("since", params.since);
  if (params.until) qs.set("until", params.until);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return `/admin/payouts/manual/export.csv${suffix}`;
}


export function fetchManualPayoutBanner(token: string, code: string) {
  return adminFetch<{ banner: string | null }>(
    `/admin/payouts/manual/banner/${encodeURIComponent(code)}`,
    token,
  );
}


export function updateManualPayoutBanner(token: string, code: string, banner: string | null) {
  return adminFetch<{ ok: true }>(
    `/admin/payouts/manual/banner/${encodeURIComponent(code)}`,
    token,
    { method: "PUT", body: JSON.stringify({ banner }) },
  );
}
